/**
 * AI Review Queue body — per-suggestion inspect · sort · gate · Approve / Dismiss.
 *
 * Extracted from FsMapping.jsx to keep the main page under the 700-line
 * mark. The component is a pure lens over `fsSuggestions` /
 * `noteSuggestions` state passed down from FsMapping — no fetching, no
 * writes here. Approve / Dismiss are wired in the parent so this stays
 * a display-only subcomponent that can be unit-tested in isolation.
 */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useMemo } from "react";
import { pendingSuggestionIds } from "@/pages/FsMapping/pendingSuggestions";   // Step 11d-3 · find 5
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function ReviewQueueBody({
  coa, fsSuggestions, noteSuggestions, dismissed,
  mode, onModeChange, sort, onSortChange,
  selected, onSelectedChange, minConfidence,
}) {
  const colW = useResizableColumns({ storageKey: "fsm-review-queue-body", defaults: [130, 110, 160, 160, 240], mins: [90, 72, 100, 100, 130] });   // Step 11d · add-on
  const { t } = useI18n();
  const suggestionsMap = mode === "fs" ? fsSuggestions : noteSuggestions;
  const coaById = useMemo(() => {
    const m = {};
    for (const c of coa) m[c.id] = c;
    return m;
  }, [coa]);

  const rows = useMemo(() => {
    const items = [];
    // Step 11d-3 · find 5: only what is pending (account still unmapped in this column, not dismissed) — the chip's count
    for (const aid of pendingSuggestionIds(coa, suggestionsMap, mode, dismissed)) {
      const s = suggestionsMap[aid];
      const c = coaById[aid];
      items.push({
        id: aid,
        account_code: c.raw_account_code || "",
        account_name: c.raw_account_name || "",
        suggestion: s,
        current: mode === "fs" ? (c.fs_mapping || "") : (c.note_mapping || ""),
      });
    }
    if (sort === "conf-asc") items.sort((a, b) => (a.suggestion.confidence || 0) - (b.suggestion.confidence || 0));
    else items.sort((a, b) => (b.suggestion.confidence || 0) - (a.suggestion.confidence || 0));
    return items;
  }, [coa, suggestionsMap, dismissed, coaById, mode, sort]);

  const fsCount = pendingSuggestionIds(coa, fsSuggestions, "fs", dismissed).length;
  const noteCount = pendingSuggestionIds(coa, noteSuggestions, "note", dismissed).length;

  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectedChange(next);
  };
  const toggleAllVisible = () => {
    if (selected.size === rows.length) onSelectedChange(new Set());
    else onSelectedChange(new Set(rows.map((r) => r.id)));
  };
  // Below-gate count (single-line to avoid confusing multi-line JSX-text detectors).
  // eslint-disable-next-line max-len
  const belowGate = rows.filter((r) => (r.suggestion.confidence || 0) < minConfidence).length;

  return (
    <div className="space-y-3" data-testid="review-queue-body">
      {/* Mode + sort + counters */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button size="sm"
                  variant={mode === "fs" ? "default" : "outline"}
                  onClick={() => onModeChange("fs")}
                  className={mode === "fs" ? "bg-violet-600 hover:bg-violet-700 text-white" : ""}
                  data-testid="review-queue-tab-fs">
            {t("b3.fsMapping")} <span className="ms-1 rounded bg-white/20 px-1 text-[10px]">{fsCount}</span>
          </Button>
          <Button size="sm"
                  variant={mode === "note" ? "default" : "outline"}
                  onClick={() => onModeChange("note")}
                  className={mode === "note" ? "bg-violet-600 hover:bg-violet-700 text-white" : ""}
                  data-testid="review-queue-tab-note">
            Notes <span className="ms-1 rounded bg-white/20 px-1 text-[10px]">{noteCount}</span>
          </Button>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {belowGate > 0 && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                  data-testid="review-queue-below-gate">
              {belowGate} below gate
            </span>
          )}
          <Select value={sort} onValueChange={onSortChange}>
            <SelectTrigger className="h-7 w-40 text-xs" data-testid="review-queue-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="conf-asc">{t("fsMappingReview.lowFirst")}</SelectItem>
              <SelectItem value="conf-desc">{t("fsMappingReview.highFirst")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      {rows.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-muted-foreground dark:border-slate-800 dark:bg-slate-900/40"
             data-testid="review-queue-empty">
          No pending {mode === "fs" ? "FS mapping" : "note mapping"} suggestions.
          Run "AI Suggest All" to populate the queue.
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto rounded-md border border-slate-200 dark:border-slate-800">
          <ResizableTable cols={colW} className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/70">
              <tr>
                <ResizableTh index={0} className="w-8 px-2 py-2">
                  <Checkbox checked={rows.length > 0 && selected.size === rows.length}
                            onCheckedChange={toggleAllVisible}
                            data-testid="review-queue-toggle-all" />
                </ResizableTh>
                <ResizableTh index={1} className="px-2 py-2 text-start">{t("common.account")}</ResizableTh>
                <ResizableTh index={2} className="px-2 py-2 text-start">{t("fsMappingReview.suggested")}</ResizableTh>
                <ResizableTh index={3} className="px-2 py-2 text-start">{t("fsMappingReview.confidence")}</ResizableTh>
                <ResizableTh index={4} className="px-2 py-2 text-start">{t("fsMappingReview.rationale")}</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const conf = r.suggestion.confidence || 0;
                const below = conf < minConfidence;
                const barColor = conf >= 0.85 ? "bg-emerald-500"
                                : conf >= 0.7  ? "bg-lime-500"
                                : conf >= 0.5  ? "bg-amber-500"
                                : "bg-red-500";
                const suggestedCode = mode === "fs"
                  ? r.suggestion.fs_mapping
                  : r.suggestion.note_mapping;
                const suggestedLabel = mode === "fs"
                  ? r.suggestion.line
                  : r.suggestion.title;
                return (
                  <tr key={r.id}
                      className={`border-t border-slate-100 dark:border-slate-800 ${
                        selected.has(r.id) ? "bg-violet-50 dark:bg-violet-950/20" : ""
                      } ${below ? "bg-amber-50/40 dark:bg-amber-950/10" : ""}`}
                      data-testid={`review-queue-row-${r.id}`}>
                    <td className="px-2 py-2">
                      <Checkbox checked={selected.has(r.id)}
                                onCheckedChange={() => toggleRow(r.id)}
                                data-testid={`review-queue-check-${r.id}`} />
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-mono text-[10px] text-muted-foreground">{r.account_code}</div>
                      <div className="text-[13px]">{r.account_name}</div>
                      {r.current && (
                        <div className="mt-0.5 text-[10px] text-muted-foreground">
                          currently: <span className="font-mono">{r.current}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-mono text-[11px]">{suggestedCode}</div>
                      <div className="text-[11px] text-muted-foreground">{suggestedLabel}</div>
                      <span className="mt-0.5 inline-block rounded bg-slate-100 px-1 py-0.5 text-[9px] font-semibold uppercase text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {r.suggestion.source}
                      </span>
                    </td>
                    <td className="px-2 py-2 w-40">
                      <div className="flex items-center gap-1">
                        <div className="h-1.5 flex-1 rounded-full bg-slate-200 dark:bg-slate-800">
                          <div className={`h-full rounded-full ${barColor}`}
                               style={{ width: `${Math.round(conf * 100)}%` }}
                               data-testid={`review-queue-bar-${r.id}`} />
                        </div>
                        <span className={`min-w-[36px] text-end font-mono text-[10px] ${ below ? "text-amber-800 font-semibold" : "text-slate-600 dark:text-slate-300" }`}>
                          {Math.round(conf * 100)}%
                        </span>
                      </div>
                      {below && (
                        <div className="mt-0.5 text-[9px] font-semibold uppercase text-amber-700">
                          below gate
                        </div>
                      )}
                    </td>
                    <td className="max-w-xs px-2 py-2 text-[11px] text-muted-foreground">
                      {r.suggestion.rationale || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ResizableTable>
        </div>
      )}
    </div>
  );
}
